'use client';

import { m, useReducedMotion } from 'motion/react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import type { FormEvent, ReactNode, Ref, RefObject } from 'react';
import { useState } from 'react';
import {
  type EarlyAccessGateAnimationHandle,
  EarlyAccessGateBackground,
} from '@/components/early-access-gate/early-access-gate-background';
import { LOGO_SRC_DARK_BACKGROUND, logoWordmarkWidthForHeight } from '@/components/logo';
import { PrizeBanner, PrizeModal } from '@/components/rewards/prize-pool';
import { PrizeTermsModal } from '@/components/rewards/prize-terms-modal';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { NewTwitterIconSolid } from '@/components/ui/icon/brands';
import { CheckmarkCircle02IconSolid, SparklesIcon } from '@/components/ui/icon/glyphs';
import { InputField } from '@/components/ui/input-field';
import { OtpField } from '@/components/ui/otp-field';
import { MOTION_ENTRANCE } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';

const CHECK_SPRING = { type: 'spring', stiffness: 480, damping: 16 } as const;

export type EarlyAccessFormState = 'idle' | 'loading' | 'error';

export interface EarlyAccessGateFormProps {
  subtitle: string;
  code: string;
  onCodeChange: (code: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  state: EarlyAccessFormState;
  inputRef?: RefObject<HTMLInputElement | null>;
  label?: string;
  placeholder?: string;
  submitLabel?: string;
  loadingLabel?: string;
  errorMessage?: string;
  footer?: ReactNode;
}

const LOGO_HEIGHT_PX = 26;

function CardCornerGlows(): ReactNode {
  return (
    <>
      <div className="pointer-events-none absolute -top-11 -left-11 size-[90px] rounded-full bg-white/20 blur-[50px]" />{' '}
      {/* ARBITRARY: Figma corner glow */}
      <div className="pointer-events-none absolute -top-11 -right-11 size-[90px] rounded-full bg-white/20 blur-[50px]" />{' '}
      {/* ARBITRARY: Figma corner glow */}
    </>
  );
}

const CARD_SHELL_CLASS =
  'relative isolate overflow-hidden rounded-[32px] border border-border-primary'; // ARBITRARY: Figma card uses 32px radius

const HEADLINE_CLASS =
  'bg-gradient-to-b from-foreground-primary to-foreground-tertiary bg-clip-text text-heading-lg font-semibold tracking-tight text-transparent';

function EarlyAccessGateCard({ children }: { children: React.ReactNode }): ReactNode {
  return (
    <div className={CARD_SHELL_CLASS}>
      <div
        className="pointer-events-none absolute inset-0 rounded-[32px] bg-background-secondary/90 backdrop-blur-sm [clip-path:inset(0_round_32px)]" /* ARBITRARY: Figma card 32px radius */
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden rounded-[32px]" /* ARBITRARY: Figma card 32px radius */
      >
        <CardCornerGlows />
      </div>
      <div className="relative z-10 flex flex-col items-center gap-5 p-6 text-center sm:gap-7 sm:p-8">
        {children}
      </div>
    </div>
  );
}

function EarlyAccessGateLogo(): ReactNode {
  return (
    <Image
      src={LOGO_SRC_DARK_BACKGROUND}
      alt="Morfi"
      width={logoWordmarkWidthForHeight(LOGO_HEIGHT_PX)}
      height={LOGO_HEIGHT_PX}
      className="block shrink-0 object-contain"
    />
  );
}

export function EarlyAccessGateShell({
  children,
  footer,
  showAnimation = true,
  overlay = false,
  animationRef,
  onRevealStart,
  onRevealComplete,
}: {
  children: React.ReactNode;
  /** Optional links pinned to the bottom of the gate (grow / partner / invest). */
  footer?: ReactNode;
  showAnimation?: boolean;
  overlay?: boolean;
  animationRef?: Ref<EarlyAccessGateAnimationHandle | null>;
  onRevealStart?: () => void;
  onRevealComplete?: () => void;
}): ReactNode {
  return (
    <main
      data-morfi-early-access-gate
      className={cn(
        'dark scheme-dark fixed inset-0 z-overlay w-full bg-background-secondary text-foreground-primary transition-opacity duration-[900ms] ease-default motion-reduce:transition-none',
        overlay && 'pointer-events-none opacity-0',
      )}
    >
      <EarlyAccessGateBackground
        showAnimation={showAnimation}
        {...(animationRef === undefined ? {} : { animationRef })}
        {...(onRevealStart === undefined ? {} : { onRevealStart })}
        {...(onRevealComplete === undefined ? {} : { onRevealComplete })}
      />
      {/* Scroll layer over the fixed background: the card centers when it fits
          and scrolls (never clips) on short / mobile-landscape viewports; the
          footer flows at the very bottom instead of overlapping the card. */}
      <div className="absolute inset-0 z-10 overflow-y-auto overscroll-contain">
        <div className="flex min-h-full flex-col items-center px-4 py-6">
          {children ? (
            <div className="flex w-full max-w-sm flex-1 items-center justify-center">
              {children}
            </div>
          ) : null}
          {footer ? (
            <div className="mt-6 flex w-full shrink-0 justify-center pb-[max(0.25rem,env(safe-area-inset-bottom))]">
              {footer}
            </div>
          ) : null}
        </div>
      </div>
    </main>
  );
}

/** Bottom-of-gate links: external "follow us on X" link + partner / invest
 *  lead-capture triggers. Wraps gracefully on narrow viewports. */
export function EarlyAccessFooter({
  followHref,
  onPartner,
  onInvest,
  followLabel,
  partnerLabel,
  investLabel,
}: {
  followHref: string;
  onPartner: () => void;
  onInvest: () => void;
  followLabel: string;
  partnerLabel: string;
  investLabel: string;
}): ReactNode {
  return (
    <nav className="flex max-w-full flex-wrap items-center justify-center gap-x-1 gap-y-1">
      <a href={followHref} target="_blank" rel="noopener noreferrer" className={FOOTER_LINK_CLASS}>
        {followLabel}
      </a>
      <FooterDot />
      <AnimatedButton
        type="button"
        variant="tertiary"
        size="sm"
        disableHoverScale
        onClick={onPartner}
        className={FOOTER_LINK_CLASS}
      >
        {partnerLabel}
      </AnimatedButton>
      <FooterDot />
      <AnimatedButton
        type="button"
        variant="tertiary"
        size="sm"
        disableHoverScale
        onClick={onInvest}
        className={FOOTER_LINK_CLASS}
      >
        {investLabel}
      </AnimatedButton>
    </nav>
  );
}

const FOOTER_LINK_CLASS =
  'inline-flex h-auto min-h-0 items-center rounded-control bg-transparent px-2 py-1 text-caption font-normal text-foreground-tertiary shadow-none transition-colors hover:bg-transparent hover:text-foreground-secondary';

function FooterDot(): ReactNode {
  return (
    <span aria-hidden="true" className="select-none text-foreground-disabled">
      ·
    </span>
  );
}

export function EarlyAccessGateForm({
  subtitle,
  code,
  onCodeChange,
  onSubmit,
  state,
  inputRef,
  label = 'Enter access code',
  placeholder = 'Enter access code',
  submitLabel = 'Enter app',
  loadingLabel = 'Verifying code...',
  errorMessage = 'Invalid access code. Please check your code and try again.',
  footer,
}: EarlyAccessGateFormProps): ReactNode {
  return (
    <EarlyAccessGateCard>
      <EarlyAccessGateLogo />

      <p className={HEADLINE_CLASS}>{subtitle}</p>

      <form onSubmit={onSubmit} className="flex w-full flex-col gap-4 text-left">
        <InputField
          ref={inputRef}
          id="beta-code"
          label={label}
          labelClassName="sr-only"
          type="text"
          value={code}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
            onCodeChange(e.target.value);
          }}
          disabled={state === 'loading'}
          placeholder={placeholder}
          autoComplete="off"
          aria-invalid={state === 'error'}
          error={state === 'error' ? errorMessage : undefined}
          className={cn(
            'rounded-control border-border-primary bg-background-secondary/90 text-sm',
            state === 'error' && 'border-feedback-error-border',
          )}
        />

        <AnimatedButton
          type="submit"
          variant="primary"
          size="lg"
          disabled={state === 'loading' || code.trim().length === 0}
          loading={state === 'loading'}
          className="h-10 w-full rounded-control disabled:opacity-30"
        >
          {state === 'loading' ? loadingLabel : submitLabel}
        </AnimatedButton>
      </form>
      {footer}
    </EarlyAccessGateCard>
  );
}

export function EarlyAccessGateWelcome({
  title = 'Welcome to Morfi Markets',
}: {
  title?: string;
}): ReactNode {
  return (
    <EarlyAccessGateShell>
      <EarlyAccessGateCard>
        <EarlyAccessGateLogo />
        <p className={HEADLINE_CLASS}>{title}</p>
      </EarlyAccessGateCard>
    </EarlyAccessGateShell>
  );
}

// ── Waitlist surfaces ─────────────────────────────────────────────────────

const SUBTLE_INPUT_CLASS =
  'rounded-control border-border-primary bg-background-secondary/90 text-sm';
const SUBMIT_BUTTON_CLASS = 'h-10 w-full rounded-control disabled:opacity-30';

/** Centered text link that flips the card between modes
 *  ("No code? Join the waitlist" / "Have a code? Enter it"). */
export function EarlyAccessModeSwitch({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}): ReactNode {
  return (
    <AnimatedButton
      type="button"
      variant="tertiary"
      size="sm"
      disableHoverScale
      onClick={onClick}
      className="h-auto min-h-0 bg-transparent px-0 py-0 text-caption font-normal text-foreground-tertiary shadow-none hover:bg-transparent hover:text-foreground-secondary"
    >
      {label}
    </AnimatedButton>
  );
}

/** Thin "or" rule between the email and X signup methods. */
function OrDivider({ label }: { label: string }): ReactNode {
  return (
    <div className="flex w-full items-center gap-2.5 text-micro tracking-widest text-foreground-tertiary uppercase">
      <span aria-hidden className="h-px flex-1 bg-border-secondary" />
      {label}
      <span aria-hidden className="h-px flex-1 bg-border-secondary" />
    </div>
  );
}

/** Pill showing the joined X identity (handle + glyph). */
function XIdentityChip({ handle }: { handle: string }): ReactNode {
  return (
    <span className="inline-flex h-8 items-center gap-2 rounded-pill border border-border-primary bg-background-primary/90 px-3 text-caption font-semibold text-foreground-primary">
      <Icon icon={NewTwitterIconSolid} className="size-3.5 text-foreground-secondary" />
      <span>@{handle}</span>
    </span>
  );
}

const X_BUTTON_CLASS =
  'h-10 w-full gap-2 rounded-control bg-foreground-primary text-background-secondary hover:bg-foreground-primary/90';

export interface EarlyAccessWaitlistFormProps {
  email: string;
  onEmailChange: (email: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  state: EarlyAccessFormState;
  errorMessage?: string | undefined;
  onSwitchToCode: () => void;
  onContinueWithX: () => void;
  /** Inline line under the X button after a cancelled/failed X return. */
  xErrorMessage?: string | undefined;
}

export function EarlyAccessWaitlistForm({
  email,
  onEmailChange,
  onSubmit,
  state,
  errorMessage,
  onSwitchToCode,
  onContinueWithX,
  xErrorMessage,
}: EarlyAccessWaitlistFormProps): ReactNode {
  const t = useTranslations('landing.waitlist');
  return (
    <EarlyAccessGateCard>
      <EarlyAccessGateLogo />
      <div className="flex flex-col items-center gap-1.5">
        <p className={HEADLINE_CLASS}>{t('heading')}</p>
        <p className="max-w-[15rem] text-caption text-foreground-secondary">{t('lead')}</p>
      </div>
      <form onSubmit={onSubmit} noValidate className="flex w-full flex-col gap-4 text-left">
        <InputField
          id="waitlist-email"
          label={t('emailLabel')}
          labelClassName="sr-only"
          type="email"
          required
          value={email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
            onEmailChange(e.target.value);
          }}
          disabled={state === 'loading'}
          placeholder={t('emailPlaceholder')}
          autoComplete="email"
          aria-invalid={state === 'error'}
          error={state === 'error' ? errorMessage : undefined}
          className={cn(SUBTLE_INPUT_CLASS, state === 'error' && 'border-feedback-error-border')}
        />
        <AnimatedButton
          type="submit"
          variant="primary"
          size="lg"
          disabled={state === 'loading' || email.trim().length === 0}
          loading={state === 'loading'}
          className={SUBMIT_BUTTON_CLASS}
        >
          {state === 'loading' ? t('sendingCode') : t('sendCode')}
        </AnimatedButton>
        <OrDivider label={t('orDivider')} />
        <AnimatedButton
          type="button"
          variant="primary"
          size="lg"
          disabled={state === 'loading'}
          onClick={onContinueWithX}
          className={X_BUTTON_CLASS}
        >
          <Icon icon={NewTwitterIconSolid} className="size-4" />
          {t('continueWithX')}
        </AnimatedButton>
        {xErrorMessage !== undefined ? (
          <p className="text-center text-micro text-feedback-error-content">{xErrorMessage}</p>
        ) : null}
        <p className="text-center text-micro text-foreground-tertiary">{t('consent')}</p>
      </form>
      <EarlyAccessModeSwitch label={t('switchToCode')} onClick={onSwitchToCode} />
    </EarlyAccessGateCard>
  );
}

export interface EarlyAccessOtpFormProps {
  /** The address the code was sent to (rendered in the sent-to line). */
  email: string;
  otp: string;
  onOtpChange: (value: string) => void;
  /** Auto-submit on the 6th digit. */
  onOtpComplete: (value: string) => void;
  onVerify: (event: FormEvent<HTMLFormElement>) => void;
  state: EarlyAccessFormState;
  errorMessage?: string | undefined;
  /** Seconds until the resend link re-arms; 0 = ready now. */
  resendSecondsLeft: number;
  /** Focus handle for the hidden input-otp control (refocus after errors). */
  inputRef?: RefObject<HTMLInputElement | null>;
  onResend: () => void;
  onChangeEmail: () => void;
}

/** mm:ss for the resend countdown (60 → "1:00", 59 → "0:59"). */
function formatCooldown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes)}:${String(seconds).padStart(2, '0')}`;
}

export function EarlyAccessOtpForm({
  email,
  otp,
  onOtpChange,
  onOtpComplete,
  onVerify,
  state,
  errorMessage,
  resendSecondsLeft,
  inputRef,
  onResend,
  onChangeEmail,
}: EarlyAccessOtpFormProps): ReactNode {
  // Distinct variable name (not `t`) so the i18n static analyser resolves
  // this component's `landing.otp` keys separately from `landing.waitlist`.
  const tOtp = useTranslations('landing.otp');
  const prefersReduced = useReducedMotion();
  // `state` is shared by verify and resend, so remember which one this form
  // started: only that button spins and relabels.
  const [lastAction, setLastAction] = useState<'verify' | 'resend'>('verify');
  const verifying = state === 'loading' && lastAction === 'verify';
  const resending = state === 'loading' && lastAction === 'resend';
  return (
    <EarlyAccessGateCard>
      <EarlyAccessGateLogo />
      <div className="flex flex-col items-center gap-1.5">
        <p className={HEADLINE_CLASS}>{tOtp('heading')}</p>
        <p className="max-w-[16rem] text-caption text-foreground-secondary">
          {tOtp('sentTo', { email })}
        </p>
      </div>
      <form
        onSubmit={(event: FormEvent<HTMLFormElement>): void => {
          setLastAction('verify');
          onVerify(event);
        }}
        noValidate
        className="flex w-full flex-col gap-4"
      >
        <m.div
          animate={
            state === 'error' && prefersReduced !== true ? { x: [0, -4, 4, -3, 3, 0] } : { x: 0 }
          }
          transition={{ duration: 0.28 }}
        >
          <OtpField
            value={otp}
            onChange={onOtpChange}
            onComplete={(value: string): void => {
              setLastAction('verify');
              onOtpComplete(value);
            }}
            disabled={state === 'loading'}
            error={state === 'error'}
            autoFocus
            {...(inputRef !== undefined ? { inputRef } : {})}
            aria-label={tOtp('emailLabelA11y')}
          />
        </m.div>
        {state === 'error' && errorMessage !== undefined ? (
          <p className="text-center text-micro text-feedback-error-content">{errorMessage}</p>
        ) : null}
        <AnimatedButton
          type="submit"
          variant="primary"
          size="lg"
          disabled={state === 'loading' || otp.length < 6}
          loading={verifying}
          className={SUBMIT_BUTTON_CLASS}
        >
          {verifying ? tOtp('verifying') : tOtp('verify')}
        </AnimatedButton>
        <p className="text-center text-caption text-foreground-tertiary">
          {tOtp('resendPrompt')}{' '}
          {resendSecondsLeft > 0 ? (
            <span className="text-foreground-disabled">
              {tOtp('resendIn', { time: formatCooldown(resendSecondsLeft) })}
            </span>
          ) : (
            <AnimatedButton
              type="button"
              variant="tertiary"
              size="sm"
              disableHoverScale
              disabled={state === 'loading'}
              loading={resending}
              onClick={(): void => {
                setLastAction('resend');
                onResend();
              }}
              className="h-auto min-h-0 bg-transparent px-0 py-0 text-caption font-medium text-foreground-brand shadow-none hover:bg-transparent"
            >
              {tOtp('resend')}
            </AnimatedButton>
          )}
        </p>
      </form>
      <EarlyAccessModeSwitch label={tOtp('changeEmail')} onClick={onChangeEmail} />
    </EarlyAccessGateCard>
  );
}

export interface EarlyAccessXEmailFormProps {
  /** X handle (without @) of the already-stored handle-only signup. */
  xHandle: string;
  email: string;
  onEmailChange: (email: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  state: EarlyAccessFormState;
  errorMessage?: string | undefined;
  onSkip: () => void;
}

/** No-email X fallback — the handle-only row already exists; adding an email
 *  is an upgrade (runs the normal OTP path), Skip is pure navigation. */
export function EarlyAccessXEmailForm({
  xHandle,
  email,
  onEmailChange,
  onSubmit,
  state,
  errorMessage,
  onSkip,
}: EarlyAccessXEmailFormProps): ReactNode {
  // Distinct variable name (not `t`) — same i18n-analyser rule as above.
  const tXJoin = useTranslations('landing.xJoin');
  const tWaitlist = useTranslations('landing.waitlist');
  return (
    <EarlyAccessGateCard>
      <EarlyAccessGateLogo />
      <div className="flex flex-col items-center gap-2.5">
        <p className={HEADLINE_CLASS}>{tXJoin('needEmailHeading')}</p>
        <XIdentityChip handle={xHandle} />
        <p className="max-w-[16rem] text-caption text-foreground-secondary">
          {tXJoin('needEmailLead')}
        </p>
      </div>
      <form onSubmit={onSubmit} noValidate className="flex w-full flex-col gap-4 text-left">
        <InputField
          id="waitlist-x-email"
          label={tWaitlist('emailLabel')}
          labelClassName="sr-only"
          type="email"
          required
          value={email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
            onEmailChange(e.target.value);
          }}
          disabled={state === 'loading'}
          placeholder={tWaitlist('emailPlaceholder')}
          autoComplete="email"
          aria-invalid={state === 'error'}
          error={state === 'error' ? errorMessage : undefined}
          className={cn(SUBTLE_INPUT_CLASS, state === 'error' && 'border-feedback-error-border')}
        />
        <AnimatedButton
          type="submit"
          variant="primary"
          size="lg"
          disabled={state === 'loading' || email.trim().length === 0}
          loading={state === 'loading'}
          className={SUBMIT_BUTTON_CLASS}
        >
          {state === 'loading' ? tWaitlist('sendingCode') : tWaitlist('sendCode')}
        </AnimatedButton>
      </form>
      <EarlyAccessModeSwitch label={tXJoin('skipKeepHandle')} onClick={onSkip} />
    </EarlyAccessGateCard>
  );
}

/**
 * The single rewards CTA on the waitlist success card. Rendered only when the
 * rewards gate is live (the handler is present, set by the container). When the
 * user already signed up with X (`authed`) it starts questing; when they signed
 * up with email (no account yet) it reads "Connect X…" and the handler runs the
 * X OAuth directly — no intermediate modal.
 */
function QuestingCta({ authed, onClick }: { authed: boolean; onClick: () => void }): ReactNode {
  const t = useTranslations('landing.waitlist');
  return (
    <AnimatedButton
      type="button"
      variant="primary"
      size="lg"
      onClick={onClick}
      className="h-10 w-full gap-2 rounded-control"
    >
      <Icon icon={authed ? SparklesIcon : NewTwitterIconSolid} className="size-4" />
      {authed ? t('startQuesting') : t('connectXToQuest')}
    </AnimatedButton>
  );
}

export interface EarlyAccessWaitlistSuccessProps {
  onSwitchToCode: () => void;
  /** Back to the signup chooser — lets a user join with another email
   *  (positions are per-email; we always show their latest signup). */
  onJoinAnother?: (() => void) | undefined;
  /** Set after an OTP verify (or X join with email) — renders the verified badge. */
  verifiedEmail?: string | null;
  /** Set after an X join — renders the identity chip. */
  xHandle?: string | null;
  /** "Start questing" handler — present only when the `rewards_platform` gate
   *  is live; the container signs the user in if needed before opening `/rewards`. */
  onStartQuesting?: (() => void) | undefined;
  /** True when the viewer already has a Morfi account (signed up with X) — flips
   *  the CTA between "Start questing" and "Connect X". */
  questingAuthed?: boolean;
}

export function EarlyAccessWaitlistSuccess({
  onSwitchToCode,
  onJoinAnother,
  verifiedEmail,
  xHandle,
  onStartQuesting,
  questingAuthed,
}: EarlyAccessWaitlistSuccessProps): ReactNode {
  const t = useTranslations('landing.waitlist');
  const prefersReduced = useReducedMotion();
  const [prizeOpen, setPrizeOpen] = useState(false);
  const [prizeTermsOpen, setPrizeTermsOpen] = useState(false);
  return (
    <EarlyAccessGateCard>
      <EarlyAccessGateLogo />
      <div className="flex flex-col items-center gap-2">
        <m.span
          initial={prefersReduced ? false : { scale: 0.4, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={prefersReduced ? { duration: 0 } : CHECK_SPRING}
        >
          <Icon
            icon={CheckmarkCircle02IconSolid}
            className="size-10 text-feedback-success-content"
          />
        </m.span>
        <m.p
          className={HEADLINE_CLASS}
          initial={prefersReduced ? false : { y: 9, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={prefersReduced ? { duration: 0 } : { ...MOTION_ENTRANCE, delay: 0.12 }}
        >
          {t('success.heading')}
        </m.p>
        {typeof xHandle === 'string' && xHandle.length > 0 ? (
          <m.span
            initial={prefersReduced ? false : { y: 9, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={prefersReduced ? { duration: 0 } : { ...MOTION_ENTRANCE, delay: 0.16 }}
          >
            <XIdentityChip handle={xHandle} />
          </m.span>
        ) : null}
        {typeof verifiedEmail === 'string' && verifiedEmail.length > 0 ? (
          <m.span
            className="inline-flex h-6 items-center gap-1.5 rounded-pill bg-feedback-success-accent/15 px-2.5 text-micro font-semibold text-feedback-success-content"
            initial={prefersReduced ? false : { y: 9, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={prefersReduced ? { duration: 0 } : { ...MOTION_ENTRANCE, delay: 0.16 }}
          >
            <Icon icon={CheckmarkCircle02IconSolid} className="size-3" />
            {t('verifiedBadge', { email: verifiedEmail })}
          </m.span>
        ) : null}
      </div>
      <div className="flex w-full flex-col gap-2.5">
        {onStartQuesting !== undefined ? (
          <>
            {/* $10k prize-pool incentive sits ABOVE the CTA — the Start-questing
                button reads as the action to claim it. */}
            <PrizeBanner onOpen={(): void => setPrizeOpen(true)} />
            <QuestingCta authed={questingAuthed ?? false} onClick={onStartQuesting} />
          </>
        ) : null}
      </div>
      {onStartQuesting !== undefined ? (
        <>
          <PrizeModal
            open={prizeOpen}
            onOpenChange={setPrizeOpen}
            onOpenTerms={(): void => {
              setPrizeOpen(false);
              setPrizeTermsOpen(true);
            }}
          />
          <PrizeTermsModal open={prizeTermsOpen} onOpenChange={setPrizeTermsOpen} />
        </>
      ) : null}
      <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
        <EarlyAccessModeSwitch label={t('switchToCode')} onClick={onSwitchToCode} />
        {onJoinAnother !== undefined ? (
          <>
            <span aria-hidden className="text-caption text-foreground-tertiary">
              ·
            </span>
            <EarlyAccessModeSwitch label={t('useAnotherEmail')} onClick={onJoinAnother} />
          </>
        ) : null}
      </div>
    </EarlyAccessGateCard>
  );
}
